import {inject, Injectable} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';
import Keycloak from 'keycloak-js';
import {environment} from '../../../environments/environment';
import {User} from '../model/user/user.model';
import {Router} from '@angular/router';
import {DebugLogger} from '../utils/debug-logger.util';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly keycloak = inject(Keycloak);
  private readonly translocoService = inject(TranslocoService);
  private readonly router = inject(Router);

  isAuthenticated(): boolean {
    return this.keycloak?.authenticated ?? false;
  }

  async login(redirectPath?: string, loginHint?: string): Promise<void> {
    try {
      const currentLang = this.translocoService.getActiveLang();

      const redirectUri = window.location.origin + (redirectPath || this.router.url);

      await this.keycloak.login({
        locale: currentLang,
        redirectUri: redirectUri,
        // Prefills the email on the Keycloak login page and is forwarded to a brokered IdP,
        // so SSO users are routed by domain (via Keycloak Organizations) without re-typing it.
        ...(loginHint ? {loginHint} : {}),
      });
      DebugLogger.log('[Auth] Login initiated successfully', {redirectUri, locale: currentLang});
    } catch (error) {
      DebugLogger.error('[Auth] Error initiating login', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      await this.keycloak.logout({
        redirectUri: environment.keycloak.postLogoutRedirectUri,
      });
      DebugLogger.log('[Auth] Logout initiated successfully', {
        redirectUri: environment.keycloak.postLogoutRedirectUri,
      });
    } catch (error) {
      DebugLogger.error('[Auth] Error initiating logout', error);
      throw error;
    }
  }

  async getUserProfile(): Promise<User | undefined> {
    if (this.keycloak?.authenticated) {
      try {
        const profile = await this.keycloak.loadUserProfile();
        return {
          name: `${profile?.firstName} ${profile.lastName}`,
          email: profile?.email,
          username: profile?.username,
        };
      } catch (error) {
        DebugLogger.error('[Auth] Error occurred while loading user profile', error);
        return undefined;
      }
    } else {
      return undefined;
    }
  }

  async updateToken(): Promise<boolean> {
    if (this.keycloak?.authenticated) {
      try {
        return await this.keycloak.updateToken(10000);
      } catch (error) {
        DebugLogger.error('[Auth] Error occurred while updating the token', error);
        return false;
      }
    } else {
      return false;
    }
  }

  async forceTokenRefresh(): Promise<boolean> {
    try {
      return await this.keycloak.updateToken(-1);
    } catch (error) {
      DebugLogger.error('[Auth] Error occurred while forcing a token refresh', error);
      return false;
    }
  }
}
